import { Autocomplete, Stack, TextField } from '@mui/material';
import * as refine from '@refinedev/core';
import { Create, Edit, useAutocomplete } from '@refinedev/mui';
import { axiosInstance } from '@refinedev/simple-rest';
import { useMemo, useState } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { uuidv7 } from 'uuidv7';
import { BaseField } from '../../../components/fields/BaseField.tsx';
import { ImageField, type ImageInfo } from '../../../components/fields/ImageField.tsx';
import { RichTextField3 } from '../../../components/fields/RichTextField3.tsx';
import { Image } from '../../../components/lib/image.ts';
import { Author } from '../../../types/index.ts';

const COMPONENT_MAP = { create: Create, edit: Edit } as const;

export const AuthorForm = ({ action }: { action: 'create' | 'edit' }) => {
  const { query, formLoading, onFinish } = refine.useForm<Author>({
    resource: 'contents/authors',
    action,
  });

  const data = useMemo(
    () => (query?.data?.data ? Author.parse(query?.data?.data) : undefined),
    [query?.data?.data],
  );

  const methods = useForm<Author>({
    values: data,
    defaultValues: {},
  });

  const Component = COMPONENT_MAP[action];

  const [imageInfo, setImageInfo] = useState<ImageInfo | null>(null);

  // Autocomplete for existing authors (only in create mode)
  const { autocompleteProps: authorAutocompleteProps } = useAutocomplete<Author>({
    resource: 'contents/authors',
    sort: [{ field: 'name', order: 'asc' }],
    onSearch: (value) => [{ field: 'name+jobTitle', operator: 'contains', value }],
    queryOptions: {
      select({ data, total }) {
        return {
          data: data.map((item) => Author.parse(item)),
          total,
        };
      },
    },
  });

  return (
    <Component
      canDelete
      isLoading={formLoading}
      saveButtonProps={{
        disabled: methods.formState.isSubmitting || formLoading,
        onClick: methods.handleSubmit(async (data) => {
          data.id ??= Author.shape.id.parse(uuidv7());

          if (data.jobTitle?.trim() === '') {
            data.jobTitle = null;
          }

          if (data.description?.trim() === '') {
            data.description = null;
          }

          if (imageInfo) {
            const contentType = 'image/webp';
            const imageKey = `images/authors/${data.id}.webp`;

            let image = await Image.fromDataURL(imageInfo.dataURL);

            if (imageInfo.crop) {
              image = image.crop(imageInfo.crop);
            }

            await fetch(`/.api/v1/contents/media/${imageKey}`, {
              method: 'POST',
              headers: {
                'content-type': contentType,
                authorization: axiosInstance.defaults.headers.common.Authorization as string,
              },
              body: await image.toBlob({ type: contentType, quality: 1 }),
            });

            data.imageKey = imageKey;
          } else {
            data.imageKey = null;
          }

          return onFinish(data);
        }),
      }}
    >
      <FormProvider {...methods}>
        <Stack spacing={2}>
          {action === 'create' && (
            <Controller
              name="name"
              control={methods.control}
              rules={{ required: '必須' }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  {...authorAutocompleteProps}
                  value={field.value ?? null}
                  onChange={(_, value) => {
                    if (value instanceof Object) {
                      methods.setValue('name', value.name);
                      methods.setValue('jobTitle', value.jobTitle || null);
                      methods.setValue('description', value.description || null);
                      methods.setValue('organization', value.organization || null);
                    }
                  }}
                  onInputChange={(_, value, reason) => {
                    authorAutocompleteProps.onInputChange(_, value, reason);

                    if (reason === 'input') {
                      field.onChange(value);
                    } else if (reason === 'selectOption') {
                      // 何もしない
                    }
                  }}
                  getOptionKey={(option) => (typeof option === 'string' ? option : option.id)}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    const jobTitlePart = option.jobTitle ? `（${option.jobTitle}）` : '';
                    const createdAtPart = option.createdAt
                      ? ` (${option.createdAt.toLocaleDateString('ja-JP')})`
                      : '';
                    return `${option.name}${jobTitlePart}${createdAtPart}`;
                  }}
                  freeSolo
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="名前"
                      variant="outlined"
                      error={!!error}
                      helperText={
                        error?.message ?? '既存の執筆者を選択するか、新しい名前を入力してください'
                      }
                    />
                  )}
                />
              )}
            />
          )}
          {action === 'edit' && (
            <BaseField
              name="name"
              rules={{
                required: '必須',
              }}
              label="名前"
              fullWidth
            />
          )}
          <BaseField name="jobTitle" label="所属" fullWidth />
          <RichTextField3 name="description" label="プロフィール" fullWidth />
          <ImageField
            label="画像"
            aspect={1}
            imageURL={data?.imageKey ? `/.api/v1/contents/media/${data.imageKey}` : null}
            onChange={setImageInfo}
          />
        </Stack>
      </FormProvider>
    </Component>
  );
};
